<template>
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="名称">
        <el-input v-model="queryParams.pointName" clearable class="!w-180px" @keyup.enter="handleQuery" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
          <el-option label="启用" :value="0" />
          <el-option label="停用" :value="1" />
          <el-option label="待审" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
        <el-button type="primary" plain @click="openForm()"><Icon icon="ep:plus" />新建</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="图片" width="80">
        <template #default="{ row }">
          <el-image
            v-if="row.picUrl"
            :src="row.picUrl"
            :preview-src-list="[row.picUrl]"
            fit="cover"
            class="h-48px w-48px rounded"
          />
          <span v-else class="text-12px text-[var(--el-text-color-secondary)]">-</span>
        </template>
      </el-table-column>
      <el-table-column label="名称" prop="pointName" min-width="140" />
      <el-table-column label="地址" prop="address" min-width="180" show-overflow-tooltip />
      <el-table-column label="联系人" prop="contactName" width="100" />
      <el-table-column label="电话" prop="contactPhone" width="120" />
      <el-table-column label="状态" width="110">
        <template #default="{ row }">
          <el-tag v-if="row.status === 2" type="warning">待审</el-tag>
          <el-switch
            v-else
            :model-value="row.status"
            :active-value="0"
            :inactive-value="1"
            @change="(v) => changeStatus(row, Number(v))"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openForm(row)">编辑</el-button>
          <el-button link type="danger" @click="handleDelete(row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="formVisible" :title="formData.id ? '编辑自提点' : '新建自提点'" width="520px">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="90px">
      <el-form-item label="名称" prop="pointName">
        <el-input v-model="formData.pointName" maxlength="64" />
      </el-form-item>
      <el-form-item label="图片">
        <UploadImg v-model="formData.picUrl" directory="groupbuy/pickup" height="110px" width="150px" />
      </el-form-item>
      <el-form-item label="地址">
        <el-input v-model="formData.address" maxlength="255" />
      </el-form-item>
      <el-form-item label="联系人">
        <el-input v-model="formData.contactName" maxlength="32" />
      </el-form-item>
      <el-form-item label="电话">
        <el-input v-model="formData.contactPhone" maxlength="20" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :value="0">启用</el-radio>
          <el-radio :value="1">停用</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="formData.remark" type="textarea" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取消</el-button>
      <el-button type="primary" :loading="formLoading" @click="submitForm">确定</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/merchant/groupbuy'
import { UploadImg } from '@/components/UploadFile'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'MerchantGroupbuyPickupPoint' })

const message = useMessage()
const loading = ref(false)
const list = ref<Api.GroupbuyPickupPointVO[]>([])
const total = ref(0)
const queryParams = reactive({ pageNo: 1, pageSize: 10, pointName: undefined as string | undefined, status: undefined as number | undefined })

const formVisible = ref(false)
const formLoading = ref(false)
const formRef = ref<FormInstance>()
const formData = ref<Api.GroupbuyPickupPointVO>({ pointName: '', status: 0 })
const rules: FormRules = { pointName: [{ required: true, message: '请输入名称', trigger: 'blur' }] }

const getList = async () => {
  loading.value = true
  try {
    const data = await Api.getPickupPointPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const openForm = (row?: Api.GroupbuyPickupPointVO) => {
  formData.value = row
    ? { ...row }
    : { pointName: '', picUrl: '', address: '', contactName: '', contactPhone: '', status: 0, remark: '' }
  formVisible.value = true
}
const submitForm = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    if (formData.value.id) {
      await Api.updatePickupPoint(formData.value)
    } else {
      await Api.createPickupPoint(formData.value)
    }
    message.success('保存成功')
    formVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}
const changeStatus = async (row: Api.GroupbuyPickupPointVO, status: number) => {
  await Api.updatePickupPointStatus(row.id!, status)
  message.success('已更新')
  await getList()
}
const handleDelete = async (id: number) => {
  await message.delConfirm()
  await Api.deletePickupPoint(id)
  message.success('已删除')
  await getList()
}

onMounted(getList)
</script>
